import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useFormContext } from "react-hook-form";
import Iconify from "components/iconify";
import customFetch from "hooks/fetchWrapper";

export default function Listing() {
  const { watch } = useFormContext();
  const selectedArea = watch("area");
  const routeSearch = watch("routeSearch") || "";
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));

  const [routes, setRoutes] = useState([]);
  const [selectedRoute, setSelectedRoute] = useState(null);
  const [busStops, setBusStops] = useState([]);
  const [loading, setLoading] = useState(false);

  // Load routes when area changes
  useEffect(() => {
    setRoutes([]);
    setSelectedRoute(null);
    setBusStops([]);

    if (!selectedArea) return;

    const loadRoutes = async () => {
      setLoading(true);
      try {
        const res = await customFetch(`routes/by-area-with-stops/${selectedArea}`);
        setRoutes(res.status && Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error loading routes:", err);
        setRoutes([]);
      } finally {
        setLoading(false);
      }
    };

    loadRoutes();
  }, [selectedArea]);

  // Filter routes based on bus stop search
  const filteredRoutes = useMemo(() => {
    return routes.filter((route) => {
      if (!routeSearch) return true;
      const searchLower = routeSearch.toLowerCase();
      return route.bustops?.some((stop) => stop.bustopname?.toLowerCase().includes(searchLower));
    });
  }, [routes, routeSearch]);

  // Handle auto-select/deselect on search or routes change
  useEffect(() => {
    setSelectedRoute(null);
    if (routeSearch && filteredRoutes.length === 1) {
      setSelectedRoute(filteredRoutes[0]);
    }
  }, [routeSearch, routes, filteredRoutes]);

  // Set bus stops from selected route (no separate API call needed)
  useEffect(() => {
    if (!selectedRoute) {
      setBusStops([]);
      return;
    }
    setBusStops(selectedRoute.bustops || []);
  }, [selectedRoute]);

  const handleCardClick = (route) => {
    setSelectedRoute(selectedRoute?.rid === route.rid ? null : route);
  };

  const openMap = (url, e) => {
    e.stopPropagation();
    if (url) window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Initial Message - Show when no area selected */}
      {!selectedArea && !loading && (
        <Box
          textAlign="center"
          sx={{
            py: 8,
            px: 4,
            bgcolor: "background.paper",
            borderRadius: 3,
            boxShadow: 2,
          }}
        >
          <Typography variant="h4" color="primary" fontWeight="bold" gutterBottom>
            Welcome to Bus Route Finder
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mt: 2 }}>
            Please select an Emirate and Area from the dropdowns above to view available bus routes
          </Typography>
        </Box>
      )}

      {/* Loading */}
      {loading && (
        <Box textAlign="center" my={6}>
          <CircularProgress size={50} sx={{ color: "#4caf50" }} />
          <Typography variant="h6" mt={2} color="text.secondary">
            Loading routes...
          </Typography>
        </Box>
      )}

      {/* No Routes */}
      {!loading && routes.length === 0 && selectedArea && (
        <Alert severity="info" sx={{ my: 4, borderRadius: 2 }}>
          No routes available for the selected area.
        </Alert>
      )}

      {/* No Search Results */}
      {!loading && routes.length > 0 && filteredRoutes.length === 0 && routeSearch && (
        <Alert severity="warning" sx={{ my: 4, borderRadius: 2 }}>
          No routes found matching "{routeSearch}". Try a different search term.
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left: Route Cards or Selected Card */}
        <Grid item xs={12} md={selectedRoute ? 4 : 12}>
          <Grid container spacing={2}>
            {selectedRoute ? (
              // Show only selected card
              <Grid item xs={12}>
                <Card
                  onClick={() => handleCardClick(selectedRoute)}
                  sx={{
                    cursor: "pointer",
                    border: "3px solid #4caf50",
                    borderRadius: 3,
                    boxShadow: 8,
                    transition: "all 0.3s ease",
                    position: "relative",
                    "&:hover": {
                      boxShadow: 10,
                    },
                  }}
                >
                  {/* Map Icon - Top Right */}
                  <Box
                    onClick={(e) => openMap(selectedRoute.map, e)}
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                      width: 50,
                      height: 50,
                      borderRadius: "50%",
                      bgcolor: "#e0e0e0",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      transition: "all 0.3s ease",
                      boxShadow: 2,
                      "&:hover": {
                        bgcolor: "#bdbdbd",
                        transform: "scale(1.1)",
                        boxShadow: 4,
                      },
                      "&:active": {
                        transform: "scale(0.95)",
                      },
                    }}
                  >
                    <img
                      src="https://img.icons8.com/?size=100&id=DcygmpZqBEd9&format=png&color=000000"
                      alt="View Map"
                      style={{ width: 28, height: 28 }}
                    />
                  </Box>

                  <CardContent sx={{ p: 2.5, pr: 9 }}>
                    <Typography variant="h5" fontWeight="bold" color="#4caf50" gutterBottom>
                      Route {selectedRoute.routeno}
                    </Typography>

                    <Typography variant="body1" gutterBottom sx={{ mb: 2, minHeight: 45 }}>
                      {selectedRoute.routename}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      <strong>Area:</strong> {selectedRoute.areas?.[0]?.areaname || "N/A"}
                      {selectedRoute.areas?.[0]?.code && ` (${selectedRoute.areas[0].code})`}
                    </Typography>

                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      <strong>Bus Number:</strong> {selectedRoute.BusNumber || "N/A"}
                    </Typography>

                    <Box mt={2} pt={2} borderTop="1px dashed #e0e0e0" textAlign="center">
                      <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                        Driver Details
                      </Typography>
                      <Typography variant="body2" mt={0.5} fontWeight="medium">
                        {selectedRoute.driver?.DriverName || "Not Assigned"}
                      </Typography>
                      {selectedRoute.driver?.ContactNumber && (
                        <Typography variant="body2" color="text.secondary" mt={0.5}>
                          {selectedRoute.driver.ContactNumber}
                        </Typography>
                      )}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ) : (
              // Show all filtered cards in grid
              filteredRoutes.map((route) => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={route.rid}>
                  <Card
                    onClick={() => handleCardClick(route)}
                    sx={{
                      cursor: "pointer",
                      border: "2px solid transparent",
                      borderRadius: 3,
                      boxShadow: 3,
                      height: "100%",
                      position: "relative",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        boxShadow: 8,
                        transform: "translateY(-4px)",
                        borderColor: "#4caf50",
                      },
                    }}
                  >
                    {/* Map Icon - Top Right */}
                    <Box
                      onClick={(e) => openMap(route.map, e)}
                      sx={{
                        position: "absolute",
                        top: 16,
                        right: 16,
                        width: 50,
                        height: 50,
                        borderRadius: "50%",
                        bgcolor: "#e0e0e0",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        boxShadow: 2,
                        "&:hover": {
                          bgcolor: "#bdbdbd",
                          transform: "scale(1.1)",
                          boxShadow: 4,
                        },
                        "&:active": {
                          transform: "scale(0.95)",
                        },
                      }}
                    >
                      <img
                        src="https://img.icons8.com/?size=100&id=DcygmpZqBEd9&format=png&color=000000"
                        alt="View Map"
                        style={{ width: 28, height: 28 }}
                      />
                    </Box>

                    <CardContent sx={{ p: 2.5, pr: 9 }}>
                      <Typography variant="h5" fontWeight="bold" color="#4caf50" gutterBottom>
                        Route {route.routeno}
                      </Typography>

                      <Typography variant="body1" gutterBottom sx={{ mb: 2, minHeight: 45 }}>
                        {route.routename}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        <strong>Area:</strong> {route.areas?.[0]?.areaname || "N/A"}
                        {route.areas?.[0]?.code && ` (${route.areas[0].code})`}
                      </Typography>

                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        <strong>Bus Number:</strong> {route.BusNumber || "N/A"}
                      </Typography>

                      <Box mt={2} pt={2} borderTop="1px dashed #e0e0e0" textAlign="center">
                        <Typography variant="subtitle2" fontWeight="bold" color="text.secondary">
                          Driver Details
                        </Typography>
                        <Typography variant="body2" mt={0.5} fontWeight="medium">
                          {route.driver?.DriverName || "Not Assigned"}
                        </Typography>
                        {route.driver?.ContactNumber && (
                          <Typography variant="body2" color="text.secondary" mt={0.5}>
                            {route.driver.ContactNumber}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          sx={{
                            mt: 1,
                            display: "inline-block",
                            color: "#1976d2", // MUI blue (you can change to "#0066cc" etc.)
                            textDecoration: "underline",
                            fontWeight: 500,
                            cursor: "pointer",
                            "&:hover": {
                              color: "#1565c0",
                              textDecoration: "none",
                            },
                          }}
                          // You can add onClick here later if this should do something specific
                          // onClick={() => alert("More driver info coming soon!")}
                        >
                          Click here for more info
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </Grid>

        {/* Right: Pickup Table (only when route selected) */}
        {selectedRoute && (
          <Grid item xs={12} md={8}>
            <Box
              sx={{
                position: { md: "sticky" },
                top: { md: 80 },
                bgcolor: "background.paper",
                borderRadius: 3,
                boxShadow: 4,
                p: 3,
              }}
            >
              <Typography
                variant="h5"
                gutterBottom
                textAlign="center"
                color="#4caf50"
                fontWeight="bold"
                sx={{ mb: 3 }}
              >
                Pickup & Drop-off Points – Route {selectedRoute.routeno}
              </Typography>

              {busStops.length > 0 ? (
                <Box sx={{ overflowX: "auto" }}>
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "60px 1fr 140px 140px 140px",
                      bgcolor: "white",
                      borderRadius: 2,
                      overflow: "hidden",
                      boxShadow: 2,
                      minWidth: 600,
                    }}
                  >
                    {/* Header */}
                    <Box
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        p: 1.5,
                        fontWeight: "bold",
                        textAlign: "center",
                        borderRight: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      #
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        p: 1.5,
                        fontWeight: "bold",
                        borderRight: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      Bus Stop
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        p: 1.5,
                        fontWeight: "bold",
                        textAlign: "center",
                        borderRight: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      Pickup Code
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        p: 1.5,
                        fontWeight: "bold",
                        textAlign: "center",
                        borderRight: "1px solid rgba(255,255,255,0.2)",
                      }}
                    >
                      Pickup Time
                    </Box>
                    <Box
                      sx={{
                        bgcolor: "#4caf50",
                        color: "white",
                        p: 1.5,
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                    >
                      Drop-off Time
                    </Box>

                    {/* Data Rows */}
                    {busStops.map((stop, i) => (
                      <React.Fragment key={stop.sid}>
                        <Box
                          sx={{
                            p: 1.5,
                            textAlign: "center",
                            bgcolor: i % 2 === 0 ? "white" : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            borderTop: "1px solid #e0e0e0",
                            fontWeight: "medium",
                          }}
                        >
                          {i + 1}
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            bgcolor: i % 2 === 0 ? "white" : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          {stop.bustopname || "—"}
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            textAlign: "center",
                            bgcolor: i % 2 === 0 ? "white" : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          {stop.pickupcode || "—"}
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            textAlign: "center",
                            bgcolor: i % 2 === 0 ? "white" : "#f5f5f5",
                            borderRight: "1px solid #e0e0e0",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          {stop.pickuptime || "—"}
                        </Box>
                        <Box
                          sx={{
                            p: 1.5,
                            textAlign: "center",
                            bgcolor: i % 2 === 0 ? "white" : "#f5f5f5",
                            borderTop: "1px solid #e0e0e0",
                          }}
                        >
                          {stop.dropofftime || "—"}
                        </Box>
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ) : (
                <Alert severity="info" sx={{ mt: 3, borderRadius: 2 }}>
                  No pickup/drop-off points available for this route.
                </Alert>
              )}

              <Box textAlign="center" mt={3}>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<Iconify icon="mdi:arrow-left-bold" />}
                  onClick={() => setSelectedRoute(null)}
                  sx={{
                    bgcolor: "#4caf50",
                    "&:hover": { bgcolor: "#388e3c" },
                    borderRadius: 2,
                    px: 6,
                    py: 1.5,
                    fontSize: "1rem",
                    fontWeight: "bold",
                  }}
                >
                  BACK TO ALL ROUTES
                </Button>
              </Box>
            </Box>
          </Grid>
        )}
      </Grid>
    </Container>
  );
}
